/**
 * Facilities, Entrega 6 (ADR 0027): serviços administrativos. Garagem (vagas, veículos e ocupação
 * com o controle do portão) e chaves (retirada, devolução, transferência, extravio, substituição e
 * baixa), sempre com o colaborador de Dados Mestres.
 */
import { z } from 'zod';

export const KEY_STATUSES = ['available', 'with_holder', 'lost', 'retired'] as const;
export type KeyStatus = (typeof KEY_STATUSES)[number];

export const KEY_MOVEMENT_KINDS = [
  'checkout',
  'return',
  'transfer',
  'lost',
  'found',
  'replaced',
  'retired',
] as const;
export type KeyMovementKind = (typeof KEY_MOVEMENT_KINDS)[number];

/** Movimentos possíveis em cada situação da chave (o banco confere de novo). */
export const KEY_MOVES_BY_STATUS: Record<KeyStatus, readonly KeyMovementKind[]> = {
  available: ['checkout', 'lost', 'replaced', 'retired'],
  with_holder: ['return', 'transfer', 'lost'],
  lost: ['found', 'replaced', 'retired'],
  retired: [],
};

/** Chave com alguém e com a devolução prevista já passada. */
export function keyOverdue(
  status: string,
  expectedReturnOn: string | null,
  today: string,
): boolean {
  return status === 'with_holder' && !!expectedReturnOn && expectedReturnOn < today;
}

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const optionalId = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');
const optionalDate = isoDate
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const code = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9][A-Z0-9_.-]{0,19}$/, 'Letras, números, ponto, hífen ou sublinhado');

export const parkingSpotInput = z.object({
  branchId: z.uuid(),
  locationId: optionalId,
  code,
  notes: optional(1000),
  isActive: z.boolean().default(true),
});

export const vehicleInput = z.object({
  employeeId: z.uuid(),
  plate: z
    .string()
    .trim()
    .transform((v) => v.toUpperCase().replace(/[^A-Z0-9]/g, ''))
    .pipe(z.string().regex(/^[A-Z0-9]{5,10}$/, 'Placa inválida')),
  model: z.string().trim().min(1).max(80),
  color: optional(40),
  isActive: z.boolean().default(true),
});

export const parkingAssignmentInput = z
  .object({
    spotId: z.uuid(),
    employeeId: z.uuid(),
    vehicleId: optionalId,
    startsOn: isoDate,
    gateControl: optional(40),
    controlDeliveredOn: optionalDate,
  })
  .refine((v) => !v.controlDeliveredOn || !!v.gateControl, {
    path: ['gateControl'],
    message: 'Identifique o controle entregue',
  });

export const parkingReleaseInput = z.object({
  endsOn: isoDate,
  endReason: z.string().trim().min(3).max(1000),
  controlReturnedOn: optionalDate,
});

export const facilityKeyInput = z.object({
  branchId: z.uuid(),
  locationId: z.uuid(),
  code,
  description: z.string().trim().min(1).max(200),
});

export const keyMovementInput = z
  .object({
    kind: z.enum(KEY_MOVEMENT_KINDS),
    employeeId: optionalId,
    expectedReturnOn: optionalDate,
    replacementKeyId: optionalId,
    note: optional(1000),
    occurredOn: isoDate,
    /** Aceite do termo de posse (provisório, seção 9): obrigatório na retirada e na transferência. */
    termAccepted: z.boolean().default(false),
  })
  .refine((v) => !['checkout', 'transfer'].includes(v.kind) || v.termAccepted, {
    path: ['termAccepted'],
    message: 'Registre o aceite do termo de posse da chave',
  })
  .refine((v) => !['checkout', 'transfer'].includes(v.kind) || !!v.employeeId, {
    path: ['employeeId'],
    message: 'Escolha o colaborador',
  })
  .refine((v) => v.kind !== 'replaced' || !!v.replacementKeyId, {
    path: ['replacementKeyId'],
    message: 'Escolha a chave nova',
  })
  .refine((v) => !['lost', 'retired', 'replaced'].includes(v.kind) || (v.note?.length ?? 0) >= 3, {
    path: ['note'],
    message: 'Explique o que aconteceu',
  });
export type KeyMovementInput = z.infer<typeof keyMovementInput>;

// ---------------------------------------------------------------------------
// Recebimento (F11) e expedição (F01), Entrega 6b
// ---------------------------------------------------------------------------

export const INBOUND_KINDS = ['correspondence', 'package', 'goods'] as const;
export const INBOUND_STATUSES = ['received', 'delivered', 'returned'] as const;
export const SHIPMENT_CARRIERS = ['correios', 'dhl', 'other'] as const;
export const SHIPMENT_STATUSES = [
  'requested',
  'sent',
  'delivered',
  'returned',
  'cancelled',
] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

/** Próximas situações da expedição (o banco confere de novo). */
export const SHIPMENT_NEXT: Record<ShipmentStatus, readonly ShipmentStatus[]> = {
  requested: ['sent', 'cancelled'],
  sent: ['delivered', 'returned'],
  delivered: [],
  returned: [],
  cancelled: [],
};

const money = z
  .object({
    amount: z
      .string()
      .trim()
      .regex(/^\d{1,15}(\.\d{1,4})?$/, 'Use números, com ponto nos decimais'),
    currency: z.string().regex(/^[A-Z]{3}$/, 'Moeda inválida'),
  })
  .nullable()
  .optional();

export const inboundItemInput = z.object({
  branchId: z.uuid(),
  kind: z.enum(INBOUND_KINDS),
  sender: optional(200),
  carrier: optional(100),
  trackingCode: optional(60),
  description: z.string().trim().min(1).max(500),
  recipientEmployeeId: optionalId,
  purchaseRequestId: optionalId,
  invoiceNumber: optional(60),
});

export const inboundCloseInput = z
  .object({
    status: z.enum(['delivered', 'returned']),
    deliveredToEmployeeId: optionalId,
    note: optional(1000),
  })
  .refine((v) => v.status !== 'delivered' || !!v.deliveredToEmployeeId, {
    path: ['deliveredToEmployeeId'],
    message: 'Escolha quem retirou',
  })
  .refine((v) => v.status !== 'returned' || (v.note?.length ?? 0) >= 3, {
    path: ['note'],
    message: 'Explique a devolução',
  });

export const shipmentInput = z
  .object({
    branchId: z.uuid(),
    originLocationId: optionalId,
    destination: z.string().trim().min(1).max(500),
    recipientName: z.string().trim().min(1).max(200),
    itemsDescription: z.string().trim().min(1).max(1000),
    assetId: optionalId,
    carrier: z.enum(SHIPMENT_CARRIERS),
    carrierName: optional(100),
    serviceLevel: optional(60),
    trackingCode: optional(60),
    invoiceNumber: optional(60),
    declared: money,
    cost: money,
    // Comprovante de postagem e etiqueta (provisório, seção 9).
    proofReference: optional(200),
    labelReference: optional(200),
  })
  .refine((v) => v.carrier !== 'other' || !!v.carrierName, {
    path: ['carrierName'],
    message: 'Informe a transportadora',
  });

export type ShipmentInput = z.infer<typeof shipmentInput>;

export const shipmentStatusInput = z
  .object({
    status: z.enum(SHIPMENT_STATUSES),
    date: optionalDate,
    trackingCode: optional(60),
    reason: optional(1000),
  })
  .refine((v) => !['sent', 'delivered'].includes(v.status) || !!v.date, {
    path: ['date'],
    message: 'Informe a data',
  })
  .refine((v) => !['returned', 'cancelled'].includes(v.status) || (v.reason?.length ?? 0) >= 3, {
    path: ['reason'],
    message: 'Explique o motivo',
  });

// ----- Ligação com a Central de Chamados -----

/** Registros operacionais que nascem de um chamado ou são ligados a ele (um por chamado). */
export const REQUEST_LINK_KINDS = ['shipment', 'inbound', 'parking', 'key', 'travel'] as const;
export type RequestLinkKind = (typeof REQUEST_LINK_KINDS)[number];
/** Modalidade do chamado de cada registro. */
export const REQUEST_LINK_SERVICE: Record<RequestLinkKind, string> = {
  shipment: 'F01',
  inbound: 'F11',
  parking: 'F10',
  key: 'F17',
  travel: 'F02',
};

/** Ligar ao chamado um registro que já existe (sem chamado). */
export const requestLinkInput = z.object({
  kind: z.enum(REQUEST_LINK_KINDS),
  recordId: z.uuid(),
});
/** Chamado de origem no cadastro do registro (opcional: o cadastro direto continua). */
export const fromRequestInput = z.object({ requestId: optionalId });

// ----- Reserva de espaços (F07, regras provisórias) -----

/** Ambiente reservável e as regras dele (quem administra os serviços ajusta). */
export const bookableSpaceInput = z.object({
  locationId: z.uuid(),
  maxHours: z.number().gt(0).max(24).default(8),
  minAdvanceHours: z.number().min(0).max(720).default(0),
  maxAdvanceDays: z.number().int().min(1).max(365).default(90),
  isActive: z.boolean().default(true),
  notes: optional(500),
});

/** Reserva: para si; quem administra os serviços pode reservar para outra pessoa. */
export const spaceBookingInput = z
  .object({
    locationId: z.uuid(),
    title: z.string().trim().min(1).max(200),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
    attendees: z.number().int().min(1).max(10000).nullable().default(null),
    requesterUserId: z.uuid().nullable().default(null),
  })
  .refine((v) => v.endsAt > v.startsAt, { path: ['endsAt'], message: 'O fim é depois do início' });

export const spaceBookingCancelInput = z.object({ reason: z.string().trim().min(3).max(500) });
